import React, { useState, useEffect, useRef } from 'react';
import { FaDownload, FaTrash, FaEye, FaEdit, FaFolderOpen, FaEllipsisV, FaTags, FaShare } from 'react-icons/fa';
import FileIcon from './FileIcon';
import LazyImage from './LazyImage';
import { formatFileSize, formatDate, isFolder, isImage, canGenerateThumbnail, isRawImage, getFileExtension, capitalizeTag } from '../utils/fileUtils';
import { fileService } from '../services/api';
import { getCategory2Tags } from '../services/predefinedTagsCache';

const FileCard = ({ 
  file, 
  isSelected, 
  onSelect, 
  onDownload, 
  onDelete, 
  onPreview, 
  onRename,
  onFolderOpen,
  onEditTags,
  onEditFolderTags,
  onShareFile
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [tags, setTags] = useState([]);
  const [tagsLoading, setTagsLoading] = useState(false);
  const [folderTags, setFolderTags] = useState([]);
  const [folderTagsLoading, setFolderTagsLoading] = useState(false);
  const [predefinedFolderTagsCategory2, setPredefinedFolderTagsCategory2] = useState([]);
  const menuRef = useRef(null);
  
  const fileName = file.Key || file.name || '';
  const displayName = file.displayName || fileName;
  const fileSize = file.Size || file.size || 0;
  const lastModified = file.LastModified || file.lastModified || '';
  const fileType = file.type || (isFolder(file) ? 'folder' : 'file');
  const itemIsFolder = isFolder(file);
  
  const handleClick = () => {
    // Si es una carpeta, abrir inmediatamente
    if (itemIsFolder && onFolderOpen) {
      onFolderOpen(file.Key || file.name || displayName);
      return;
    }
    
    // Para todos los archivos (imagen, PDF, texto, etc.), abrir en modal de vista previa
    if (onPreview) {
      onPreview(file);
      return;
    }
  };

  const handleMenuClick = (e) => {
    e.stopPropagation();
    setShowMenu(!showMenu);
  };

  const handleMenuAction = (e, action) => {
    e.stopPropagation();
    setShowMenu(false);
    action();
  };

  // Cerrar menú al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Cargar tags solo para archivos (no carpetas)
  useEffect(() => {
    if (!itemIsFolder && fileName) {
      setTagsLoading(true);
      fileService.getFileTags(fileName)
        .then(fileTags => {
          setTags(fileTags || []);
        })
        .catch(err => {
          console.warn('Error cargando tags:', err);
          setTags([]);
        })
        .finally(() => {
          setTagsLoading(false);
        });
    }
  }, [fileName, itemIsFolder]);

  // Cargar tags predefinidos de categoría 2 para determinar colores (desde cache)
  useEffect(() => {
    let mounted = true;
    getCategory2Tags()
      .then(category2Tags => {
        if (mounted) {
          setPredefinedFolderTagsCategory2(category2Tags);
        }
      })
      .catch(err => {
        console.warn('Error cargando tags predefinidos de carpetas desde cache:', err);
      });
    return () => { mounted = false; };
  }, []);

  // Cargar tags solo para carpetas
  useEffect(() => {
    if (itemIsFolder && fileName) {
      setFolderTagsLoading(true);
      fileService.getFolderTags(fileName)
        .then(fTags => {
          setFolderTags(fTags || []);
        })
        .catch(err => {
          console.warn('Error cargando tags de carpeta:', err);
          setFolderTags([]);
        })
        .finally(() => {
          setFolderTagsLoading(false);
        });
    }
  }, [fileName, itemIsFolder]);



  // Diseño especial para carpetas (rectangular horizontal)
  if (itemIsFolder) {
    return (
      <div
        className={`
          relative bg-main-card rounded-lg p-3 border cursor-pointer transition-all duration-200
          ${isSelected ? 'ring-2 ring-blue-500 bg-blue-900/20' : ''}
          ${isHovered ? 'border-blue-500' : 'border-gray-300'}
          flex items-start space-x-3 min-h-[60px]
        `}
        onClick={handleClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Checkbox para selección */}
        <div className="flex-shrink-0 pt-1">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onSelect && onSelect(fileName)}
            className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
            onClick={(e) => e.stopPropagation()}
          />
        </div>

        {/* Ícono de carpeta */}
        <div className="flex-shrink-0">
          <FileIcon 
            fileName={file.Key || file.name || fileName} 
            itemType={fileType}
            size="text-3xl"
          />
        </div>

        {/* Contenedor para nombre y tags */}
        <div className="flex-1 min-w-0 flex flex-col">
          {/* Nombre de la carpeta - arriba */}
          <h3 
            className="text-gray-900 text-sm font-medium truncate w-full mb-1"
            title={displayName}
          >
            {displayName}
          </h3>
          
          {/* Tags de carpeta - abajo en una fila */}
          {folderTagsLoading ? (
            <div className="flex items-center gap-1 text-xs text-gray-500">
              <div className="w-3 h-3 border border-gray-400 border-t-transparent rounded-full animate-spin"></div>
              <span>Cargando tags...</span>
            </div>
          ) : folderTags.length > 0 && (
            <div className="flex flex-wrap items-center gap-1">
              {folderTags.map((tag, idx) => {
                const displayTag = capitalizeTag(tag);
                // Normalizar el tag para comparar (los tags del backend vienen normalizados)
                const normalizeTagForComparison = (t) => {
                  if (!t || typeof t !== 'string') return null;
                  return t.toLowerCase().trim().replace(/\s+/g, '-');
                };
                const normalizedTag = normalizeTagForComparison(tag);
                // Verificar si el tag pertenece a la categoría 2 (verde)
                // Los tags predefinidos tienen espacios, así que los normalizamos para comparar
                const isCategory2 = predefinedFolderTagsCategory2.length > 0 && predefinedFolderTagsCategory2.some(
                  predefinedTag => {
                    const normalizedPredefined = normalizeTagForComparison(predefinedTag);
                    return normalizedPredefined === normalizedTag;
                  }
                );
                
                return (
                  <span
                    key={idx}
                    className={`text-xs px-2 py-0.5 rounded-full font-medium border whitespace-nowrap ${
                      isCategory2
                        ? 'bg-green-100 text-green-800 border-green-200'
                        : 'bg-yellow-100 text-yellow-800 border-yellow-200'
                    }`}
                    title={displayTag}
                  >
                    {displayTag}
                  </span>
                );
              })}
            </div>
          )}
        </div>

        {/* Menú de 3 puntitos */}
        <div className="flex-shrink-0 relative" ref={menuRef}>
          <button
            onClick={handleMenuClick}
            className="p-2 hover:bg-gray-600 rounded text-gray-400 hover:text-white transition-colors"
            title="Más opciones"
          >
            <FaEllipsisV />
          </button>
         
          {/* Dropdown del menú */}
          {showMenu && (
            <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-10 min-w-[140px]">
              <button
                onClick={(e) => handleMenuAction(e, () => onFolderOpen && onFolderOpen(displayName))}
                className="group w-full flex items-center space-x-2 px-3 py-2 text-sm text-gray-900 hover:text-blue-500 hover:bg-blue-500/10 transition-colors rounded-t-lg"
              >
                <FaFolderOpen className="text-gray-600 group-hover:text-blue-500 transition-colors"  />
                <span>Abrir</span>
              </button>
              <button
                onClick={(e) => handleMenuAction(e, () => onEditFolderTags && onEditFolderTags(file))}
                className="group w-full flex items-center space-x-2 px-3 py-2 text-sm text-gray-900 hover:text-blue-500 hover:bg-blue-500/10 transition-colors rounded-t-lg"
              >
                <FaTags className="text-gray-600 group-hover:text-blue-500 transition-colors"  />
                <span>Etiquetas</span>
              </button>
              <button
                onClick={(e) => handleMenuAction(e, () => onRename && onRename(file))}
                className="group w-full flex items-center space-x-2 px-3 py-2 text-sm text-gray-900 hover:text-blue-500 hover:bg-blue-500/10 transition-colors rounded-t-lg"
              >
                <FaEdit className="text-gray-600 group-hover:text-blue-500 transition-colors"  />
                <span>Renombrar</span>
              </button>
              <button
                onClick={(e) => handleMenuAction(e, () => onDelete && onDelete(file))}
                className="group w-full flex items-center space-x-2 px-3 py-2 text-sm text-gray-900 hover:text-blue-500 hover:bg-blue-500/10 transition-colors rounded-t-lg"
              >
                <FaTrash className="text-gray-600 group-hover:text-blue-500 transition-colors"  />
                <span>Eliminar</span>
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Diseño normal para archivos (vertical/cuadrado)
  return (
    <div
      className={`
        relative bg-main-card rounded-lg p-6 border cursor-pointer transition-all duration-200
        ${isSelected ? 'ring-2 ring-blue-500 bg-blue-900/20' : ''}
        ${isHovered ? 'border-blue-500' : 'border-gray-300'}
      `}
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Header con checkbox y menú */}
      <div className="flex justify-between items-start mb-4">
        {/* Checkbox para selección */}
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => onSelect && onSelect(fileName)}
          className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
          onClick={(e) => e.stopPropagation()}
        />

        {/* Menú de 3 puntitos */}
        <div className="relative" ref={menuRef}>
          <button
            onClick={handleMenuClick}
            className="p-1 hover:bg-gray-600 rounded text-gray-400 hover:text-white transition-colors"
            title="Más opciones"
          >
            <FaEllipsisV />
          </button>

          {/* Dropdown del menú */}
          {showMenu && (
            <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-10 min-w-[140px]">
              <button
                onClick={(e) => handleMenuAction(e, () => onPreview && onPreview(file))}
                className="group w-full flex items-center space-x-2 px-3 py-2 text-sm text-gray-900 hover:text-blue-500 hover:bg-blue-500/10 transition-colors rounded-t-lg"
              >
                <FaEye className="text-gray-600 group-hover:text-blue-500 transition-colors" />
                <span>Vista previa</span>
              </button>
              <button
                onClick={(e) => handleMenuAction(e, () => onDownload && onDownload(file))}
                className="group w-full flex items-center space-x-2 px-3 py-2 text-sm text-gray-900 hover:text-blue-500 hover:bg-blue-500/10 transition-colors rounded-t-lg"
              >
                <FaDownload className="text-gray-600 group-hover:text-blue-500 transition-colors" />
                <span>Descargar</span>
              </button>
              <button
                onClick={(e) => handleMenuAction(e, () => onShareFile && onShareFile(file))}
                className="group w-full flex items-center space-x-2 px-3 py-2 text-sm text-gray-900 hover:text-blue-500 hover:bg-blue-500/10 transition-colors rounded-t-lg"
              >
                <FaShare className="text-gray-600 group-hover:text-blue-500 transition-colors" />
                <span>Compartir</span>
              </button>
              <button
                onClick={(e) => handleMenuAction(e, () => onEditTags && onEditTags(file))}
                className="group w-full flex items-center space-x-2 px-3 py-2 text-sm text-gray-900 hover:text-blue-500 hover:bg-blue-500/10 transition-colors rounded-t-lg"
              >
                <FaTags className="text-gray-600 group-hover:text-blue-500 transition-colors" />
                <span>Etiquetas</span>
              </button>
              <button
                onClick={(e) => handleMenuAction(e, () => onRename && onRename(file))}
                className="group w-full flex items-center space-x-2 px-3 py-2 text-sm text-gray-900 hover:text-blue-500 hover:bg-blue-500/10 transition-colors rounded-t-lg"
              >
                <FaEdit className="text-gray-600 group-hover:text-blue-500 transition-colors" />
                <span>Renombrar</span>
              </button>
              <button
                onClick={(e) => handleMenuAction(e, () => onDelete && onDelete(file))}
                className="group w-full flex items-center space-x-2 px-3 py-2 text-sm text-gray-900 hover:text-blue-500 hover:bg-blue-500/10 transition-colors rounded-t-lg"
              >
                <FaTrash className="text-gray-600 group-hover:text-blue-500 transition-colors" />
                <span>Eliminar</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Contenido principal */}
      <div className="flex flex-col items-center space-y-4">
        {/* Thumbnail para imágenes o ícono para otros archivos */}
        <div className="flex justify-center items-center w-full h-48">
          {isImage(fileName) ? (
            canGenerateThumbnail(fileName) ? (
              // Thumbnail para imágenes con soporte
              <LazyImage
                fileName={fileName}
                alt={displayName}
                className="w-full h-full"
                width={200}
                height={200}
                quality={10}
                fallbackIcon={
                  <div className="w-full h-full flex flex-col items-center justify-center bg-gray-800 text-gray-300">
                    <FileIcon 
                      fileName={fileName} 
                      itemType={fileType}
                      size="text-4xl"
                    />
                    <span className="text-xs mt-2">No se pudo cargar</span>
                  </div>
                }
              />
            ) : (
              // Diseño elegante para archivos RAW (sin intentar thumbnail)
              <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-purple-900 via-purple-800 to-blue-900 rounded-lg text-white shadow-lg">
                <div className="text-6xl mb-3">📸</div>
                <div className="px-3 py-1 bg-purple-600 rounded-full text-sm font-bold shadow-md mb-2">
                  RAW
                </div>
                <span className="text-lg font-semibold mb-1">
                  {getFileExtension(fileName).toUpperCase()}
                </span>
                <span className="text-xs opacity-75 text-center px-2">
                  Archivo de imagen profesional
                </span>
              </div>
            )
          ) : (
            // Ícono normal para archivos que no son imágenes
            <FileIcon 
              fileName={fileName} 
              itemType={fileType}
              size="text-5xl"
            />
          )}
        </div>

        {/* Nombre del archivo */}
        <div className="text-center w-full px-2">
          <h3 
            className="text-gray-900 text-sm font-medium truncate w-full"
            title={displayName}
          >
            {displayName}
          </h3>
          {isRawImage(fileName) && (
            <div className="flex items-center justify-center mt-1">
              <span className="text-xs px-2 py-1 bg-purple-100 text-purple-800 rounded-full font-medium border border-purple-200">
                📸 Imagen Profesional RAW
              </span>
            </div>
          )}
          
          {/* Tags */}
          {tags.length > 0 && (
            <div className="flex flex-wrap items-center justify-center gap-1 mt-2">
              {tags.map((tag, idx) => {
                // Función para capitalizar la primera letra de cada palabra
                const capitalizeTag = (tag) => {
                  if (!tag || typeof tag !== 'string') return tag;
                  // Si el tag tiene guiones, capitalizar cada palabra separada por guión
                  if (tag.includes('-')) {
                    return tag
                      .split('-')
                      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                      .join(' ');
                  }
                  // Si no tiene guiones, capitalizar solo la primera letra
                  return tag.charAt(0).toUpperCase() + tag.slice(1).toLowerCase();
                };
                
                const displayTag = capitalizeTag(tag);
                
                return (
                  <span
                    key={idx}
                    className="text-xs px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full font-medium border border-blue-200"
                    title={displayTag}
                  >
                    {displayTag}
                  </span>
                );
              })}
            </div>
          )}
        </div>

        {/* Información adicional */}
        <div className="text-center">
          <p className="text-gray-400 text-xs">
            {formatFileSize(fileSize)}
            {lastModified && (
              <span className="text-gray-500"> | {formatDate(lastModified)}</span>
            )}
          </p>
        </div>
      </div>
    </div>
  );
};

export default FileCard; 